/**
 * useFindContacts — the "Find contact" row action (single entry) and the
 * batch find-contacts campaign action. Both only kick off a BE job/
 * operation; the outcome (a contact found, or still none) arrives over SSE
 * via useOutreachSSE, not from this mutation's own response — so onSuccess
 * here just invalidates rows so the row can pick up a `syncing` state while
 * the job is in flight.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSSE } from '@discovr/common';
import { campaignKeys } from '../queryKeys';
import { findContactForEntry, findContactsForCampaign } from '../api/outreachApi';

export function useFindContact(campaignId: string) {
  const queryClient = useQueryClient();

  return useMutation<{ jobId: string }, Error, string>({
    mutationFn: (entryId) => findContactForEntry(entryId, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    },
  });
}

export type FindContactPhase = 'looking' | 'failed';

interface ContactsFoundPayload {
  campaign_id?: string;
  job_id?: string;
}

interface AgentJobFailedPayload {
  campaign_id?: string;
  job_id?: string;
  roster_entry_id?: string;
  code?: string;
}

const FAILURE_LABELS: Record<string, string> = {
  deadline_exceeded: 'Search timed out',
  upstream_failed: 'Search provider unavailable',
  upstream_unreachable: 'Search provider unavailable',
  unavailable: 'Search unavailable, try again',
  agent_busy: 'Search busy, try again',
  invalid_request: "Couldn't search this creator",
};

/** Plain copy for the BE's failure `code` (the part before any colon); never model or provider text. */
export function findContactFailureLabel(code?: string): string {
  const head = code?.split(':')[0] ?? '';
  return FAILURE_LABELS[head] ?? 'Search failed';
}

export const FIND_CONTACT_FAILURE_DISPLAY_MS = 4000;

/** Backstop for the "looking" phase when its terminal SSE event is lost.
 *  Must outlast the BE's own give-up point, or the row reads "Search failed"
 *  while the hunt is still running: internal/outreach/jobs/sweep_agent_jobs.go
 *  fails a job after 3 dispatches × 15m webhook deadline (~48m), and River's
 *  in-dispatch retries can stretch that to ~96m. */
export const FIND_CONTACT_LOOKING_TIMEOUT_MS = 2 * 60 * 60 * 1000;

/** The BE's event name (internal/sse/event.go) — a dot, not an underscore. */
const AGENT_JOB_FAILED_EVENT = 'outreach_agent_job.failed';

/**
 * useFindContactStatus — wraps useFindContact with a per-entry "Looking…" /
 * "Search failed" phase, so ContactCell's row-scoped Find contact button can
 * show a real-time status instead of going quiet the instant the POST
 * returns (the hunt itself finishes later, async).
 *
 * "Found"/"not found" has no explicit phase of its own: once a tracked job's
 * terminal outreach_contacts_found event arrives, the phase just clears and
 * the row's own re-render (already triggered by that same event's
 * invalidation, and again on outreach_entry_projected, via useOutreachSSE)
 * shows whichever is true — the resolved email, or "No email found".
 *
 * The in-flight ref (not just the phases state) is what makes repeat clicks
 * a no-op: it's read-then-set synchronously, so it closes the race a
 * state-only check would leave open between two clicks in the same tick.
 *
 * Every timer this hook schedules is keyed by entryId in a ref map, so a
 * fresh attempt for that same entry always cancels whatever timer the
 * previous attempt left armed — otherwise a stale "failed" auto-clear (or a
 * stale "looking" timeout) fires later and wipes out a newer attempt's
 * state out from under it.
 *
 * Each attempt awaits its own mutateAsync promise: per-call mutate() callbacks
 * only fire for the latest call on a shared observer, so a second row clicked
 * mid-request would orphan the first. A contacts_found that beats its own POST
 * response (Go's payload has no entry id) is held until that response maps it.
 */
export function useFindContactStatus(campaignId: string) {
  const { mutateAsync } = useFindContact(campaignId);
  const [phases, setPhases] = useState<Record<string, FindContactPhase>>({});
  const [failureCodes, setFailureCodes] = useState<Record<string, string>>({});
  const inFlight = useRef<Set<string>>(new Set());
  const jobToEntry = useRef<Map<string, string>>(new Map());
  const pendingPosts = useRef(0);
  const mounted = useRef(true);
  const earlyFound = useRef<Set<string>>(new Set());
  const failureTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const lookingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    mounted.current = true;
    return () => {
      // A POST settling after unmount must not re-arm a timer this cleanup already cleared.
      mounted.current = false;
      failureTimers.current.forEach(clearTimeout);
      failureTimers.current.clear();
      lookingTimers.current.forEach(clearTimeout);
      lookingTimers.current.clear();
    };
  }, []);

  const clearPhase = useCallback((entryId: string) => {
    setPhases((prev) => {
      if (!(entryId in prev)) return prev;
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
  }, []);

  const cancelLookingTimer = useCallback((entryId: string) => {
    const existing = lookingTimers.current.get(entryId);
    if (existing) {
      clearTimeout(existing);
      lookingTimers.current.delete(entryId);
    }
  }, []);

  const scheduleFailureClear = useCallback(
    (entryId: string) => {
      const existing = failureTimers.current.get(entryId);
      if (existing) clearTimeout(existing);
      failureTimers.current.set(
        entryId,
        setTimeout(() => {
          failureTimers.current.delete(entryId);
          clearPhase(entryId);
        }, FIND_CONTACT_FAILURE_DISPLAY_MS),
      );
    },
    [clearPhase],
  );

  // Settling an entry (found, failed, or a looking-timeout) always means:
  // no job is tracked for it any more, it's free to be re-dispatched, and
  // any pending looking-timeout for it is moot.
  const settle = useCallback(
    (entryId: string, jobId?: string) => {
      if (jobId) jobToEntry.current.delete(jobId);
      inFlight.current.delete(entryId);
      cancelLookingTimer(entryId);
    },
    [cancelLookingTimer],
  );

  const fail = useCallback(
    (entryId: string, jobId?: string, code?: string) => {
      settle(entryId, jobId);
      setPhases((prev) => ({ ...prev, [entryId]: 'failed' }));
      setFailureCodes((prev) => ({ ...prev, [entryId]: code ?? '' }));
      scheduleFailureClear(entryId);
    },
    [settle, scheduleFailureClear],
  );

  const findContact = useCallback(
    (entryId: string) => {
      if (inFlight.current.has(entryId)) return; // already looking — ignore the repeat click
      inFlight.current.add(entryId);
      // A fresh attempt supersedes anything an earlier attempt for this same
      // entry left scheduled — otherwise a stale failure auto-clear can fire
      // later and wipe out this attempt's "looking" phase out from under it.
      const existingFailureTimer = failureTimers.current.get(entryId);
      if (existingFailureTimer) {
        clearTimeout(existingFailureTimer);
        failureTimers.current.delete(entryId);
      }
      cancelLookingTimer(entryId);
      setPhases((prev) => ({ ...prev, [entryId]: 'looking' }));
      lookingTimers.current.set(
        entryId,
        setTimeout(() => fail(entryId), FIND_CONTACT_LOOKING_TIMEOUT_MS),
      );
      pendingPosts.current += 1;
      mutateAsync(entryId)
        .then(({ jobId }) => {
          if (!mounted.current) return;
          if (earlyFound.current.delete(jobId)) {
            settle(entryId, jobId);
            clearPhase(entryId);
          } else {
            jobToEntry.current.set(jobId, entryId);
          }
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console -- the only client-side trace of why a find-contact attempt failed
          console.error('[useFindContactStatus] find-contact request failed', { entryId, err });
          if (mounted.current) fail(entryId);
        })
        .finally(() => {
          pendingPosts.current -= 1;
          if (pendingPosts.current === 0) earlyFound.current.clear();
        });
    },
    [mutateAsync, fail, settle, clearPhase, cancelLookingTimer],
  );

  const onContactsFound = useCallback(
    (payload: ContactsFoundPayload) => {
      if (payload.campaign_id !== campaignId || !payload.job_id) return;
      const entryId = jobToEntry.current.get(payload.job_id);
      if (!entryId) {
        // Possibly ours, its POST still in flight; bulk-operation job ids also land here and are dropped once no POST is pending.
        if (pendingPosts.current > 0) earlyFound.current.add(payload.job_id);
        return;
      }
      settle(entryId, payload.job_id);
      clearPhase(entryId);
    },
    [campaignId, settle, clearPhase],
  );

  const onAgentJobFailed = useCallback(
    (payload: AgentJobFailedPayload) => {
      if (payload.campaign_id !== campaignId || !payload.job_id) return;
      const entryId = jobToEntry.current.get(payload.job_id) ?? payload.roster_entry_id;
      if (!entryId) return;
      fail(entryId, payload.job_id, payload.code);
    },
    [campaignId, fail],
  );

  useSSE('outreach_contacts_found', !!campaignId, onContactsFound);
  useSSE(AGENT_JOB_FAILED_EVENT, !!campaignId, onAgentJobFailed);

  const phaseFor = useCallback((entryId: string): FindContactPhase | undefined => phases[entryId], [phases]);

  const failureLabelFor = useCallback(
    (entryId: string): string | undefined => (phases[entryId] === 'failed' ? findContactFailureLabel(failureCodes[entryId]) : undefined),
    [phases, failureCodes],
  );

  return { findContact, phaseFor, failureLabelFor };
}

export function useFindContactsBatch(campaignId: string) {
  const queryClient = useQueryClient();

  return useMutation<{ operationId: string }, Error, string[] | undefined>({
    mutationFn: (entryIds) => findContactsForCampaign(campaignId, entryIds, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    },
  });
}
